import { useState } from "react";
import { Contract } from "../types";
import { ArrowRight, Trash2, Download, Loader2 } from "lucide-react";
import {
  PdfDownloadPopup,
  PdfPopupState,
  downloadBankingContractPdf,
} from "./PdfDownloadPopup";

export function ContractCard({
  contract,
  active,
  onClick,
  onDelete,
}: {
  contract: Contract;
  active: boolean;
  onClick: () => void;
  onDelete?: () => void;
}) {
  const [downloading, setDownloading] = useState(false);
  const [pdfPopup, setPdfPopup] = useState<PdfPopupState | null>(null);

  // Contract ids in this feature are prefixed; the API takes the raw trailing id.
  const actualId = contract.id.split("-").pop() || contract.id;

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setPdfPopup(null);
    try {
      setPdfPopup(await downloadBankingContractPdf(actualId));
    } finally {
      setDownloading(false);
    }
  };

  const showActions = active;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={`group relative text-left px-3 py-3 cursor-pointer flex flex-col gap-2 transition-colors w-full border-l-4 ${
        active
          ? "bg-[#FDF1E8C2] border-l-[#F1935C]"
          : "bg-white border-l-transparent hover:bg-[#F9FAFB]"
      }`}
    >
      {/* Zone 1 — header: parties + status dot, then LoA number */}
      <div className="flex flex-col gap-1">
        <div className="flex items-start justify-between gap-2">
          <div className="text-[12px] font-semibold text-[#525062] flex items-center flex-wrap flex-1 min-w-0 gap-1">
            <span>{contract.from}</span>
            <ArrowRight className="w-3.5 h-3.5 text-[#E2682B] shrink-0" />
            <span>{contract.to}</span>
          </div>
          <StatusDot status={contract.status} />
        </div>
        {contract.loa && (
          <div className="text-[11px] text-[#828A99] leading-tight">
            <span className="font-medium">LoA</span>{" "}
            <span className="text-[#525062] font-medium break-all">
              {contract.loa}
            </span>
          </div>
        )}
      </div>

      <div className="border-t border-dashed border-[#E7E7E7] w-full" />

      {/* Zone 2 — metrics */}
      <div className="grid grid-cols-3 gap-4">
        <Metric label="Supply Period" value={String(contract.supplyPeriod)} />
        <Metric
          label="Return Period"
          value={String(contract.returnPeriod)}
          highlight
        />
        <Metric label="Banking Factor" value={contract.bf} />
      </div>
      {contract.traderName && (
        <div className="border-t border-dashed border-[#E7E7E7] w-full" />
      )}
      {contract.traderName && (
        <Metric label="Trader Name" value={contract.traderName} />
      )}

      {/* Zone 3 — footer actions (active card only): Download left, Delete right */}
      {showActions && (
        <>
          <div className="border-t border-dashed border-[#E7E7E7] w-full" />
          <div className="flex items-center justify-between">
            <button
              type="button"
              title="Download contract PDF"
              aria-label="Download contract PDF"
              disabled={downloading}
              onClick={(e) => {
                e.stopPropagation();
                handleDownload();
              }}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#E07B43] hover:text-[#C85F28] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed transition-colors bg-transparent border-none p-0"
            >
              {downloading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
              {downloading ? "Downloading…" : "Download"}
            </button>
            {onDelete && (
              <button
                type="button"
                title="Delete contract"
                aria-label="Delete contract"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#E54848] hover:text-[#C8311C] cursor-pointer transition-colors bg-transparent border-none p-0"
              >
                <Trash2 size={14} />
                Delete
              </button>
            )}
          </div>
        </>
      )}
      <PdfDownloadPopup popup={pdfPopup} onClose={() => setPdfPopup(null)} />
    </div>
  );
}

function StatusDot({ status }: { status: Contract["status"] }) {
  const isActive = status === "ACTIVE";
  return (
    <span className="inline-flex items-center gap-1.5 shrink-0">
      <span
        className={`w-2 h-2 rounded-full ${
          isActive ? "bg-[#12B76A]" : "bg-[#E54848]"
        }`}
      />
      <span
        className={`text-[11px] font-semibold ${
          isActive ? "text-[#0A8F4F]" : "text-[#C8311C]"
        }`}
      >
        {isActive ? "Active" : "Inactive"}
      </span>
    </span>
  );
}

function Metric({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-[11px] text-[#828A99] font-medium leading-tight">
        {label}
      </div>
      <div
        className={`text-[12px] font-bold ${
          highlight ? "text-[#C8311C]" : "text-[#525062]"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

export default ContractCard;
